import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  api, hasAccessToken, isNetworkError, isOnline, onConnectivity, onSessionLostDo, probeServer, setAccessToken,
} from './client'
import type { AuthResponse, Me } from './identity'
import { AuthContext } from './session'
import type { AuthState, Session } from './session'

// ── الخروجُ بعد الخمول — طلبُ المالك ٢٠٢٦-١٠-١٢ ───────────────────────────
//
// «يطلع من اليوزر تلقائي بعد ساعتين من عدم الشغل». جهازٌ تُرك مفتوحًا والموظّفُ
// روّح جلسةٌ يبيع بها من يمرّ. والساعتان تُقاس من آخر لمسة: فأرة، لوحة مفاتيح،
// إصبع، **وماسحُ الباركود** (لوحةُ مفاتيح هو).
//
// ⚠ **والوقتُ يُحفظ على الجهاز** لا في الذاكرة: برنامجٌ أُغلق ليلةً وفُتح صباحًا
//   لا يدخل بجلسة أمس — يُسأل عن كلمته.
//
// ⚠ **ولا خروجَ وهو دون اتّصال**: الدخولُ يحتاج الخادم، وخروجٌ هنا يوقف البيعَ
//   حتّى يعود النت. ويقع الخروجُ أوّلَ ما يشبك إن كانت الساعتان قد مضتا.

const IDLE_LIMIT_MS = 2 * 60 * 60 * 1000
const ACTIVITY_KEY = 'shop:last-activity'

function lastActivity(): number | null {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY)

    return raw ? Number(raw) : null
  } catch {
    return null
  }
}

function touchActivity() {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()))
  } catch {
    // تخزينٌ ممنوع: يبقى العدُّ في هذا التبويب وحدَه حتّى يُغلق.
  }
}

function idleTooLong(): boolean {
  const last = lastActivity()

  return last !== null && Date.now() - last > IDLE_LIMIT_MS
}

/** نسخةُ «من أنا» على الجهاز — <b>لنقطة البيع وحدَها، وللعمل دون اتّصال وحدَه</b>. */
const ME_KEY = 'shop:me'

function cachedMe(): Me | null {
  try {
    const raw = localStorage.getItem(ME_KEY)

    return raw ? (JSON.parse(raw) as Me) : null
  } catch {
    return null
  }
}

function rememberMe(me: Me | null) {
  try {
    if (me) {
      localStorage.setItem(ME_KEY, JSON.stringify(me))
    } else {
      localStorage.removeItem(ME_KEY)
    }
  } catch {
    // مساحةٌ ممتلئةٌ أو تخزينٌ ممنوع: العملُ دون اتّصال يتعطّل، والمتّصلُ لا.
  }
}

/**
 * حالةُ الجلسة في الواجهة.
 *
 * <b>ولا شيءَ منها يُحفظ في `localStorage`</b> — لا الرمزُ ولا الاسمُ ولا
 * الصلاحيّات. الصلاحيّاتُ خاصّةً: نسخةٌ محفوظةٌ منها تبقى بعد سحبِ صلاحيّةٍ من
 * الموظّف، فيرى أزرارًا تردّ 403، ويظنّ العطبَ في النظام لا في حقّه. وهي تُقرأ
 * من `/me` في كلّ تحميلِ تطبيق، ومن الخادم في كلّ طلبٍ فعليّ.
 *
 * ⚠ <b>إلا في نقطة البيع حين تعمل دون اتّصال</b> (<c>offlineCapable</c>، <c>0052</c>):
 *   «من أنا» تُحفظ على الجهاز — والرمزُ لا — <b>ولا تُقرأ إلا والخادمُ لا يجيب</b>.
 *   فما دام متّصلًا تبقى الصلاحيّاتُ من `/me` كما كانت، وما يُباع فاصلًا يُرحَّل
 *   بعدها تحت رمزٍ حقيقيّ يفحصه الخادم من جديد.
 */
export function AuthProvider({
  children,
  offlineCapable = false,
}: {
  children: ReactNode
  offlineCapable?: boolean
}) {
  const [session, setSession] = useState<Session | null | undefined>(undefined)
  const [endedByIdle, setEndedByIdle] = useState(false)

  const loadMe = useCallback(async (mustChange: boolean) => {
    const me = await api<Me>('/api/v1/auth/me')

    if (offlineCapable) {
      rememberMe(me)
    }

    setSession({ me, mustChangePassword: mustChange, offline: false })
  }, [offlineCapable])

  /** يرجع بلا جلسةٍ من الخادم — <b>فاصلًا من نسخة الجهاز إن أمكن</b>. */
  const fallBack = useCallback((failure: unknown) => {
    const me = offlineCapable && isNetworkError(failure) ? cachedMe() : null

    setSession(me ? { me, mustChangePassword: false, offline: true } : null)
  }, [offlineCapable])

  // ① عند فتح التطبيق: رمزُ الوصول في الذاكرة، والذاكرةُ فرغت بتحديث الصفحة.
  //    فيُجرَّب التدويرُ بالكوكي مرّةً واحدة. ونجاحُه يعني أنّ الموظّف يعود إلى
  //    حيث كان بلا أن يُعيد كتابةَ كلمته في كلّ تحديثِ صفحة — وهو ثمنُ إبقاء
  //    الرمز في الذاكرة، وهذا ما يدفعه.
  const resume = useCallback(async (isCancelled: () => boolean = () => false) => {
    try {
      const tokens = await api<AuthResponse>('/api/v1/auth/refresh', {
        method: 'POST',
        body: {},
        anonymous: true,
      })

      if (isCancelled()) {
        return
      }

      // ⚠ **جلسةٌ نامت أكثرَ من ساعتين لا تُستأنف** — حتّى لو كان كوكيُّها حيًّا.
      if (idleTooLong()) {
        setAccessToken(tokens.accessToken)
        await api<void>('/api/v1/auth/logout', { method: 'POST' }).catch(() => undefined)
        setAccessToken(null)
        rememberMe(null)
        setEndedByIdle(true)
        setSession(null)

        return
      }

      setAccessToken(tokens.accessToken)
      await loadMe(tokens.mustChangePassword)
    } catch (failure) {
      if (!isCancelled()) {
        fallBack(failure)
      }
    }
  }, [loadMe, fallBack])

  useEffect(() => {
    let cancelled = false

    void resume(() => cancelled)

    return () => {
      cancelled = true
    }
  }, [resume])

  // ② وحين تسقط الجلسةُ في منتصف العمل (حسابٌ عُطّل، أو رُفعت نسخةُ صلاحيّاته)
  //    تُخرجه الواجهةُ فورًا. وبلا هذا يبقى أمام شاشةٍ كلُّ زرٍّ فيها يردّ 401.
  useEffect(() => {
    onSessionLostDo(() => setSession(null))

    return () => onSessionLostDo(null)
  }, [])

  // ③ الاتّصالُ ينقطع ويعود — <b>ونقطةُ البيع تتبعه</b> (`0052`): تتحوّل إلى
  //    «دون اتّصال» حين لا يصل نداءٌ، وتسأل الخادمَ كلَّ ١٥ ثانيةً حتّى يجيب،
  //    ثمّ تستأنف الجلسةَ برمزٍ جديد.
  useEffect(() => {
    if (!offlineCapable) {
      return
    }

    const stop = onConnectivity((online) => {
      setSession((current) => (current ? { ...current, offline: !online } : current))

      if (online && !hasAccessToken()) {
        void resume()
      }
    })

    const timer = window.setInterval(() => {
      if (!isOnline()) {
        void probeServer()
      }
    }, 15_000)

    return () => {
      stop()
      window.clearInterval(timer)
    }
  }, [offlineCapable, resume])

  const login = useCallback(
    async (username: string, password: string) => {
      touchActivity()
      setEndedByIdle(false)

      const tokens = await api<AuthResponse>('/api/v1/auth/login', {
        method: 'POST',
        anonymous: true,

        // ‏`clientType: 'WEB'` إلزاميّ ولا يُستنبط: هو ما يقرّر أن يخرج رمزُ
        // التحديث إلى كوكي `HttpOnly` بدل جسم الردّ.
        body: { username, password, clientType: 'WEB', platform: 'WEB' },
      })

      setAccessToken(tokens.accessToken)
      await loadMe(tokens.mustChangePassword)
    },
    [loadMe],
  )

  const logout = useCallback(async () => {
    try {
      await api<void>('/api/v1/auth/logout', { method: 'POST' })
    } catch {
      // خروجٌ فشل نداؤه خروجٌ مع ذلك: الرمزُ يُمحى من الذاكرة هنا، والكوكي
      // تموت بانتهائها. وإبقاءُ الموظّف داخلًا لأنّ الشبكةَ تعطّلت أسوأُ.
    } finally {
      setAccessToken(null)
      rememberMe(null)
      setSession(null)
    }
  }, [])

  // ④ الخمول: كلُّ لمسةٍ تُسجَّل (مرّةً كلَّ ٣٠ ثانيةً على الأكثر)، وكلَّ دقيقةٍ
  //    يُسأل: أمضت ساعتان؟
  useEffect(() => {
    if (!session) {
      return
    }

    let written = 0

    const onActivity = () => {
      const now = Date.now()

      if (now - written > 30_000) {
        written = now
        touchActivity()
      }
    }

    if (lastActivity() === null) {
      touchActivity()
    }

    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'] as const
    events.forEach((name) => window.addEventListener(name, onActivity, { passive: true }))

    const timer = window.setInterval(() => {
      if (!session.offline && idleTooLong()) {
        setEndedByIdle(true)
        void logout()
      }
    }, 60_000)

    return () => {
      events.forEach((name) => window.removeEventListener(name, onActivity))
      window.clearInterval(timer)
    }
  }, [session, logout])

  const passwordChanged = useCallback(() => {
    setSession((current) => (current ? { ...current, mustChangePassword: false } : current))
  }, [])

  const refreshMe = useCallback(async () => {
    if (hasAccessToken()) {
      const me = await api<Me>('/api/v1/auth/me')
      setSession((current) => (current ? { ...current, me } : current))
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      session,
      login,
      logout,
      passwordChanged,
      refreshMe,
      can: (permission: string) => session?.me.permissions.includes(permission) ?? false,
      endedByIdle,
    }),
    [session, login, logout, passwordChanged, refreshMe, endedByIdle],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
